import { create } from 'zustand';
import {
  loginUser,
  registerUser,
  refreshToken as refreshTokenService,
  getUsers,
  getUser as getUserById,
  listPlatformUsers as listPlatformUsersService,
  updateUser,
  createUser,
  deleteUser,
  forgotPassword,
  getCurrentUser,
  replaceUserCollectionSites as replaceUserCollectionSitesService,
} from '../services/auth.service';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  UserSchema,
  UpdateUserRequest,
  ForgotPasswordRequest,
  AuthTokenPayload,
  UserAdminListParams,
  UserAdminPagination,
} from '../types/auth.type';
import { jwtDecode } from 'jwt-decode';
import {
  canAccess,
  getAccessibleOrgIds,
  hasPermission as checkPermission,
  isPlatformAdmin,
  normalizeTokenPayload,
  resolvePermissionList,
  isTokenPermVerStale,
  canAccessAdminApp,
} from '../auth';
import {
  clearAnalyticsUser,
  setAnalyticsUser,
  trackLogin,
} from '../helpers/analytics.helper';
import { parseApiError } from '../helpers/api-error.helper';

interface AuthState {
  isAuthenticated: boolean;
  user: UserSchema | null;
  tokenPayload: AuthTokenPayload | null;
  permissionList: string[];
  accessibleOrgIds: string[];
  isPlatformAdmin: boolean;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  permVerStale: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<LoginResponse | null>;
  register: (data: RegisterRequest) => Promise<UserSchema | null>;
  logout: () => void;
  refreshUserToken: (data: RefreshTokenRequest) => Promise<LoginResponse | null>;
  syncFromToken: (accessToken: string) => void;
  hydrateFromMe: () => Promise<void>;
  hasPermission: (required: string) => boolean;
  canAccessPermission: (
    required: string,
    legacyRoles?: readonly string[],
    orgId?: string | null,
  ) => boolean;
  getAllUsers: (organizationId?: string) => Promise<UserSchema[]>;
  getOrganizationUsers: (organizationId: string) => Promise<UserSchema[]>;
  listPlatformUsers: (params?: UserAdminListParams) => Promise<UserAdminPagination | null>;
  getUser: (userId: string) => Promise<UserSchema | null>;
  createNewUser: (data: RegisterRequest) => Promise<UserSchema | null>;
  updateOrganizationUser: (
    organizationId: string,
    userId: string,
    data: UpdateUserRequest,
  ) => Promise<UserSchema | null>;
  replaceUserCollectionSites: (
    organizationId: string,
    userId: string,
    collection_site_ids: string[],
  ) => Promise<UserSchema | null>;
  removeUser: (userId: string) => Promise<boolean>;
  initAuth: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

const ADMIN_PILOT_ACCESS_MESSAGE =
  'Admin is not available in Pilot Mode. Please contact a platform administrator.';

function buildUserFromToken(
  decoded: Record<string, unknown>,
  payload: AuthTokenPayload,
): UserSchema {
  const data = (decoded.data as UserSchema['data']) ?? {};
  return {
    sub: payload.sub,
    username: payload.username,
    exp: payload.exp,
    iat: payload.iat,
    ...decoded,
    data: {
      ...data,
      permissions: data.permissions ?? payload.permissions,
      sites: data.sites ?? payload.sites,
    },
  } as UserSchema;
}

function clearStoredAuth(
  set: (partial: Partial<AuthState> | ((state: AuthState) => Partial<AuthState>)) => void,
  error: string | null = null,
): void {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  set({
    token: null,
    refreshToken: null,
    tokenPayload: null,
    permissionList: [],
    accessibleOrgIds: [],
    isPlatformAdmin: false,
    user: null,
    isAuthenticated: false,
    isAdmin: false,
    permVerStale: false,
    error,
  });
}

function applyAuthFromToken(
  accessToken: string,
  refreshTokenValue: string | null,
  set: (partial: Partial<AuthState> | ((state: AuthState) => Partial<AuthState>)) => void,
): AuthTokenPayload | null {
  const decoded = jwtDecode<Record<string, unknown>>(accessToken);
  const payload = normalizeTokenPayload(decoded);

  if (payload.is_pilot === true) {
    clearStoredAuth(set, ADMIN_PILOT_ACCESS_MESSAGE);
    return null;
  }

  if (!canAccessAdminApp(payload)) {
    clearStoredAuth(set);
    return null;
  }

  const permissionList = resolvePermissionList(payload);
  const platformAdmin = isPlatformAdmin(payload);
  const user = buildUserFromToken(decoded, payload);

  set({
    token: accessToken,
    refreshToken: refreshTokenValue,
    tokenPayload: payload,
    permissionList,
    accessibleOrgIds: getAccessibleOrgIds(payload),
    isPlatformAdmin: platformAdmin,
    user,
    isAuthenticated: true,
    isAdmin: platformAdmin,
  });

  return payload;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  tokenPayload: null,
  permissionList: [],
  accessibleOrgIds: [],
  isPlatformAdmin: false,
  token: null,
  refreshToken: null,
  isLoading: false,
  isAdmin: false,
  permVerStale: false,
  error: null,

  syncFromToken: (accessToken) => {
    const refreshTokenValue = get().refreshToken ?? sessionStorage.getItem('refresh_token');
    applyAuthFromToken(accessToken, refreshTokenValue, set);
  },

  hydrateFromMe: async () => {
    try {
      const response = await getCurrentUser();
      const data = response as GenericResponse<UserSchema>;
      if (!data?.result) {
        return;
      }
      const fromApi = data.result;
      set((state) => {
        const mePerms = fromApi.data?.permissions;
        const existingPayload = state.tokenPayload;
        const permissionList = resolvePermissionList(existingPayload, mePerms ?? null);
        const serverPermVer = fromApi.data?.perm_ver;
        const permVerStale = isTokenPermVerStale(existingPayload?.perm_ver, serverPermVer);
        const mergedUser: UserSchema = {
          ...fromApi,
          sub: fromApi.sub ?? fromApi.id ?? state.user?.sub,
          platform_personas: fromApi.platform_personas ?? state.user?.platform_personas,
          data: {
            ...(fromApi.data ?? {}),
            permissions:
              mePerms && (Array.isArray(mePerms) ? mePerms.length > 0 : Object.keys(mePerms).length > 0)
                ? mePerms
                : state.user?.data?.permissions,
            sites: fromApi.data?.sites ?? state.user?.data?.sites,
            perm_ver: serverPermVer ?? fromApi.data?.perm_ver,
          },
        };
        return {
          user: mergedUser,
          permissionList,
          permVerStale,
          accessibleOrgIds: existingPayload
            ? getAccessibleOrgIds(existingPayload)
            : state.accessibleOrgIds,
        };
      });
    } catch {
      // Keep JWT-derived state if /user/me fails
    }
  },

  hasPermission: (required) => {
    const { permissionList, isPlatformAdmin: platformAdmin } = get();
    if (platformAdmin) {
      return true;
    }
    return checkPermission(permissionList, required);
  },

  canAccessPermission: (required, legacyRoles, orgId) => {
    const { tokenPayload, permissionList } = get();
    return canAccess(tokenPayload, permissionList, required, {
      orgId: orgId ?? undefined,
      legacyRoles,
    });
  },

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await loginUser(credentials);
      const data = response as GenericResponse<LoginResponse>;

      const payload = applyAuthFromToken(
        data.result.access_token,
        data.result.refresh_token,
        set,
      );

      if (!payload) {
        set((state) => ({
          error: state.error ?? 'You are not authorized to access the admin platform.',
          isLoading: false,
        }));
        return null;
      }

      sessionStorage.setItem('access_token', data.result.access_token);
      sessionStorage.setItem('refresh_token', data.result.refresh_token);

      await get().hydrateFromMe();

      if (payload.sub) {
        setAnalyticsUser(payload.sub);
      }
      trackLogin();

      set({ isLoading: false });
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: unknown } } };
      const parsed = parseApiError(err.response?.data?.detail, 'Failed to login');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      set({
        error: parsed.message,
        isLoading: false,
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        user: null,
        tokenPayload: null,
        permissionList: [],
        accessibleOrgIds: [],
        isPlatformAdmin: false,
        isAdmin: false,
      });
      return null;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await registerUser(data);
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to register',
        isLoading: false,
      });
      return null;
    }
  },

  refreshUserToken: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await refreshTokenService(data);
      const responseData = response as GenericResponse<LoginResponse>;

      const payload = applyAuthFromToken(
        responseData.result.access_token,
        responseData.result.refresh_token,
        set,
      );

      if (!payload) {
        set((state) => ({
          error: state.error ?? 'You are not authorized to access the admin platform.',
          isLoading: false,
        }));
        return null;
      }

      sessionStorage.setItem('access_token', responseData.result.access_token);
      sessionStorage.setItem('refresh_token', responseData.result.refresh_token);

      await get().hydrateFromMe();
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to refresh token',
        isLoading: false,
      });
      return null;
    }
  },

  getAllUsers: async (organizationId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUsers(organizationId);
      const data = response as GenericResponse<UserSchema[]>;
      set({ isLoading: false });
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to get users',
        isLoading: false,
      });
      return [];
    }
  },

  getOrganizationUsers: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUsers(organizationId);
      const data = response as GenericResponse<UserSchema[]>;
      set({ isLoading: false });
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to get users',
        isLoading: false,
      });
      return [];
    }
  },

  listPlatformUsers: async (params = {}) => {
    try {
      const response = await listPlatformUsersService(params);
      const data = response as GenericResponse<UserAdminPagination>;
      const result = data.result;
      if (!result) {
        return null;
      }
      return {
        ...result,
        users: (result.users ?? []).map((u) => {
          const id = u.id ?? u.sub;
          return {
            ...u,
            id,
            sub: u.sub ?? u.id ?? id,
          };
        }),
      };
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      throw err;
    }
  },

  getUser: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUserById(userId);
      const data = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return data.result ?? null;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to get user',
        isLoading: false,
      });
      return null;
    }
  },

  createNewUser: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createUser(data);
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to create user',
        isLoading: false,
      });
      return null;
    }
  },

  updateOrganizationUser: async (organizationId, userId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateUser(organizationId, userId, data);
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to update user',
        isLoading: false,
      });
      return null;
    }
  },

  replaceUserCollectionSites: async (organizationId, userId, collection_site_ids) => {
    set({ isLoading: true, error: null });
    try {
      const response = await replaceUserCollectionSitesService(
        organizationId,
        userId,
        collection_site_ids,
      );
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to update collection sites',
        isLoading: false,
      });
      return null;
    }
  },

  removeUser: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteUser(userId);
      set({ isLoading: false });
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to delete user',
        isLoading: false,
      });
      return false;
    }
  },

  logout: () => {
    clearAnalyticsUser();

    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    set({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      tokenPayload: null,
      permissionList: [],
      accessibleOrgIds: [],
      isPlatformAdmin: false,
      isAdmin: false,
      permVerStale: false,
      error: null,
    });
  },

  initAuth: async () => {
    try {
      set({ isLoading: true });
      const accessToken = sessionStorage.getItem('access_token');
      const refreshTokenValue = sessionStorage.getItem('refresh_token');

      if (!accessToken || !refreshTokenValue) {
        set({ isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const decoded = jwtDecode<{ exp?: number }>(accessToken);

        if (decoded.exp) {
          const expirationTime = decoded.exp * 1000;

          if (Date.now() >= expirationTime) {
            const response = await refreshTokenService({ refresh_token: refreshTokenValue });
            const responseData = response as GenericResponse<LoginResponse>;

            if (responseData?.result?.access_token) {
              const payload = applyAuthFromToken(
                responseData.result.access_token,
                responseData.result.refresh_token,
                set,
              );
              if (!payload) {
                set({ isAuthenticated: false, isLoading: false });
                return;
              }
              sessionStorage.setItem('access_token', responseData.result.access_token);
              sessionStorage.setItem('refresh_token', responseData.result.refresh_token);
              await get().hydrateFromMe();
              if (payload.sub) {
                setAnalyticsUser(payload.sub);
              }
              set({ isLoading: false });
              return;
            }

            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('refresh_token');
            set({ isAuthenticated: false, isLoading: false });
            return;
          }
        }

        const payload = applyAuthFromToken(accessToken, refreshTokenValue, set);
        if (!payload) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }
        await get().hydrateFromMe();
        if (payload.sub) {
          setAnalyticsUser(payload.sub);
        }
        set({ isLoading: false });
      } catch (error) {
        console.error('Error checking token expiration:', error);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({ isAuthenticated: false, isLoading: false });
    }
  },

  forgotPassword: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await forgotPassword(data);
      set({ isLoading: false });
      return true;
    } catch {
      set({ isLoading: false });
      return true;
    }
  },
}));

/** Called from axios interceptor after silent token refresh. */
export function syncAuthStoreFromToken(accessToken: string): void {
  useAuthStore.getState().syncFromToken(accessToken);
}
